import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Input, Tooltip } from "antd";
import { FilterFilled, SearchOutlined } from "@ant-design/icons";

export interface ColumnFilterChecklistProps {
  options: string[];
  selectedValues: string[];
  onApply: (values: string[]) => void;
  onClose: () => void;
  searchPlaceholder?: string;
  width?: number;
  maxListHeight?: number;
}

export const ColumnFilterChecklist: React.FC<ColumnFilterChecklistProps> = ({
  options,
  selectedValues,
  onApply,
  onClose,
  searchPlaceholder = "Search...",
  width = 240,
  maxListHeight = 220,
}) => {
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<string[]>(selectedValues);

  useEffect(() => {
    setDraft(selectedValues);
    setQuery("");
  }, [selectedValues]);

  const filtered = useMemo(
    () =>
      options.filter((o) =>
        o.toLowerCase().includes(query.trim().toLowerCase())
      ),
    [options, query]
  );

  const draftSet = useMemo(() => new Set(draft), [draft]);

  const filteredSelected = useMemo(
    () => filtered.reduce((acc, v) => (draftSet.has(v) ? acc + 1 : acc), 0),
    [filtered, draftSet]
  );

  const allChecked = filtered.length > 0 && filteredSelected === filtered.length;
  const someChecked = filteredSelected > 0 && filteredSelected < filtered.length;

  const toggleOne = (value: string) => {
    setDraft((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]
    );
  };

  const toggleAll = (checked: boolean) => {
    const names = filtered;
    if (checked) {
      setDraft((prev) => Array.from(new Set([...prev, ...names])));
    } else {
      const set = new Set(names);
      setDraft((prev) => prev.filter((n) => !set.has(n)));
    }
  };

  return (
    <div style={{ width, padding: 12 }}>
      <Input
        prefix={
          <SearchOutlined
            style={{ color: "#999" }}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        }
        placeholder={searchPlaceholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginBottom: 8 }}
        allowClear
      />
      <div
        style={{
          borderBottom: "1px solid #f0f0f0",
          paddingBottom: 8,
          marginBottom: 8,
        }}
      >
        <Checkbox
          indeterminate={someChecked}
          checked={allChecked}
          onChange={(e) => toggleAll(e.target.checked)}
          disabled={filtered.length === 0}
        >
          Select All
        </Checkbox>
      </div>
      <div
        style={{
          maxHeight: maxListHeight,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        {filtered.length === 0 ? (
          <div style={{ color: "#999", textAlign: "center", padding: "12px 0" }}>
            No results
          </div>
        ) : (
          filtered.map((value) => (
            <div key={value}>
              <Checkbox
                checked={draftSet.has(value)}
                onChange={() => toggleOne(value)}
              >
                {value}
              </Checkbox>
            </div>
          ))
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTop: "1px solid #f0f0f0",
          marginTop: 8,
          paddingTop: 8,
        }}
      >
        <Button type="link" onClick={() => setDraft([])} style={{ padding: 0 }}>
          Reset
        </Button>
        <Button
          type="primary"
          onClick={() => {
            onApply(draft);
            onClose();
          }}
        >
          Ok ({draft.length} Selected)
        </Button>
      </div>
    </div>
  );
};

export const renderColumnFilterIcon = (
  label: string,
  hasSelection: boolean,
  onTooltipOpenChange?: (open: boolean) => void,
) => () => (
  <Tooltip
    title={`Filter By ${label}`}
    mouseEnterDelay={0}
    mouseLeaveDelay={0}
    overlayStyle={{ pointerEvents: "none" }}
    onOpenChange={onTooltipOpenChange}
  >
    <FilterFilled
      style={{ color: hasSelection ? "#1677ff" : "#bfbfbf" }}
      onPointerEnterCapture={undefined}
      onPointerLeaveCapture={undefined}
    />
  </Tooltip>
);

export default ColumnFilterChecklist;
